import { db } from "@/db/schema"
import type { DbObject, DbFile } from "@/db/schema"
import { useLiveQueryImmediate } from "@/db/hooks"
import { checklistParentPath, queryChecklistItems } from "@/db/operations"

/**
 * Subscribe to all checklist items for a task, sorted by index.
 *
 * Uses `useLiveQueryImmediate` (non-suspending) so the list never
 * flashes/disappears when items are added or removed. The current
 * array stays visible until the new query result arrives.
 *
 * Returns `[items, setOptimistic, isLoading]` — use `setOptimistic`
 * for immediate DnD/split/merge feedback before the DB write lands.
 */
export function useChecklistItems(
  taskId: string,
  taskParentPath: string,
): [
  DbObject[],
  React.Dispatch<React.SetStateAction<DbObject[] | null>>,
  boolean,
] {
  const parentPath = checklistParentPath(taskParentPath, taskId)

  return useLiveQueryImmediate(
    // Pass `db`, read at query time: GuestSharePage swaps this live binding to
    // guestDb, while the ops context behind getDb() keeps the main instance.
    () => queryChecklistItems(parentPath, db),
    ["checklist-items", parentPath],
    [] as DbObject[],
  )
}

/**
 * Subscribe to a single checklist item's data by id.
 *
 * Used inside memoized ChecklistItem/ChecklistHeading components
 * so that edits to one item only re-render THAT item's component,
 * not the entire list. This is the key multi-user optimization.
 */
export function useChecklistItemData(
  id: string,
): [
  DbObject | undefined,
  React.Dispatch<React.SetStateAction<DbObject | undefined | null>>,
  boolean,
] {
  return useLiveQueryImmediate(
    () => db.totodo.get(id),
    ["checklist-item", id],
    undefined,
  )
}

/**
 * Subscribe to files attached to a checklist item.
 * Returns metadata + thumbnail (no full content) for display.
 */
export function useChecklistFiles(
  itemId: string | undefined,
): [Pick<DbFile, "id" | "name" | "mimeType" | "thumbnail">[], boolean] {
  const [files, , isLoading] = useLiveQueryImmediate(
    () => {
      if (!itemId) return Promise.resolve([])
      return db.files
        .where("parentId")
        .equals(itemId)
        .toArray()
        .then((rows) =>
          rows.map(({ id, name, mimeType, thumbnail }) => ({
            id,
            name,
            mimeType,
            thumbnail,
          })),
        )
    },
    ["checklist-files", itemId ?? ""],
    [] as Pick<DbFile, "id" | "name" | "mimeType" | "thumbnail">[],
  )
  return [files, isLoading]
}
